import { PointCloudSubscene } from "@/components/r3f/effects/point-cloud-subscene";
import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { PointCloudObject } from "@/object-cache";
import { useAutoClipBox } from "@/tools/use-auto-clip-box";
import { isObjPointCloudPoint } from "@/types/threejs-type-guards";
import { useBoxControlsContext } from "@/utils/box-controls-context";
import {
  CopyToScreenPass,
  EffectPipelineWithSubScenes,
  FilteredRenderPass,
  StoreFboPass,
  StoreFboPassObj,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { useRef, type JSX } from "react";
import { Object3D } from "three";

type ExportModeRenderingPipelineProps = {
  /** The point cloud to be rendered */
  pointCloudObject: PointCloudObject;
  /** Callback issued when the auto clip box function is invoked */
  onAutoClipBox(obb: OrientedBoundingBox): void;
  /** Callback issued when the reset clip box button is clicked */
  onResetClipBox(): void;
};

/** @returns A rendering pipeline for the export mode */
export function ExportModeRenderingPipeline({
  pointCloudObject,
  onAutoClipBox,
  onResetClipBox,
}: ExportModeRenderingPipelineProps): JSX.Element {
  const { resetBoxEvent, autoBoxEvent } = useBoxControlsContext();

  // Reference to an offscreen FBO to store the depth buffer
  const storeFboRef = useRef<StoreFboPassObj>(null);

  // Function that will create a clipping box by sampling the depth buffer
  const autoClipBoxFnc = useAutoClipBox(storeFboRef, onAutoClipBox);

  // Assign the auto clipping box function to the autoClipBox event
  // This event is triggered when the user clicks on the auto clip button
  useTypedEvent(autoBoxEvent, autoClipBoxFnc);

  /** Function to call when the reset clipping box button is clicked */
  useTypedEvent(resetBoxEvent, onResetClipBox);

  return (
    <EffectPipelineWithSubScenes>
      <PointCloudSubscene pointCloud={pointCloudObject} />
      <StoreFboPass ref={storeFboRef} />
      <FilteredRenderPass
        filter={(obj: Object3D) => !isObjPointCloudPoint(obj)}
        clear={false}
        clearDepth={false}
      />
      <CopyToScreenPass />
    </EffectPipelineWithSubScenes>
  );
}
